'use client'

import { useState, useEffect, useRef } from 'react'
import { useParams, useRouter } from 'next/navigation'
import { useLocale } from 'next-intl'
import { Loader2, ArrowRight } from '@/components/ui/icons'
import { getPromoCampaign, recordPromoAttribution, type PromoCampaignPublic } from '@/lib/api'
import { trackFirstMonthPromoShown } from '@/lib/analytics'
import { getPromoDealPlans, PLAN_MONTHLY_OLIVES, PLAN_NAMES, PLAN_PRICES } from './promoDeal'
import { SignUpModal } from '@/components/auth'
import { useAuth } from '@/hooks/useAuth'
import { storePromoSlug } from '@/lib/signupAccessStorage'
import { Button } from '@/components/ui/button'
import { VideoBackground } from '@/components/VideoBackground'

export function PromoLandingClient() {
  const params = useParams()
  const router = useRouter()
  const locale = useLocale()
  const slug = params.slug as string
  const { isAuthenticated, isAnonymous, isLoading: authLoading } = useAuth()

  const [campaign, setCampaign] = useState<PromoCampaignPublic | null>(null)
  const [showSignUpModal, setShowSignUpModal] = useState(false)
  const [modalMode, setModalMode] = useState<'signup' | 'signin'>('signup')
  const [isLoading, setIsLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const hasStored = useRef(false)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsVisible(true)
  }, [])

  // Fetch campaign info and store slug
  useEffect(() => {
    if (!slug) return

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsLoading(true)
    getPromoCampaign(slug)
      .then(data => {
        if (data) {
          setCampaign(data)
          if (data.active && !hasStored.current) {
            storePromoSlug(slug)
            hasStored.current = true
          }
        } else {
          setNotFound(true)
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setIsLoading(false))
  }, [slug])

  // Audit trail: 50%-off strike rendered on this surface (once per mount, one
  // event per plan row that shows it — mirrors the deal-box render condition).
  const promoShownRef = useRef(false)
  useEffect(() => {
    if (promoShownRef.current) return
    // Only the audience that actually SEES the deal box. While auth is loading
    // the render is still a spinner; a signed-in visitor is redirected to
    // /select-plan by the effect above and never sees this box — counting either
    // would inflate the display→checkout audit.
    if (authLoading || (isAuthenticated && !isAnonymous)) return
    if (!campaign || campaign.firstMonthPromoActive !== true) return
    // Same selector the box renders from, so the audit can never record an
    // impression the visitor didn't see (or miss one they did).
    const shownPlans = getPromoDealPlans(campaign)
    if (shownPlans.length === 0) return
    promoShownRef.current = true
    for (const planId of shownPlans) trackFirstMonthPromoShown({ planId, source: 'promo_landing_page' })
  }, [campaign, authLoading, isAuthenticated, isAnonymous])

  // Redirect authenticated users: active campaigns → select-plan, expired → projects.
  useEffect(() => {
    if (!authLoading && isAuthenticated && !isAnonymous && campaign && slug) {
      if (campaign.active) {
        recordPromoAttribution(slug, 'landing_page').then(() => {
          router.replace('/select-plan')
        })
      } else {
        router.replace('/projects')
      }
    }
  }, [authLoading, isAuthenticated, isAnonymous, campaign, slug, router])

  // Redirect on not found
  useEffect(() => {
    if (notFound) {
      router.replace('/')
    }
  }, [notFound, router])

  if (authLoading || (isLoading && slug)) {
    return (
      <div className="relative flex min-h-screen items-center justify-center bg-black">
        <Loader2 className="h-8 w-8 animate-spin text-white/50" />
      </div>
    )
  }

  if (!campaign || notFound) {
    return null
  }

  const inviterName = campaign.landing_config?.inviter_name
  const headline = campaign.landing_config?.headline ?? 'Your director\u2019s chair is ready.'
  const description =
    campaign.landing_config?.description ??
    'The film set for AI videos. Direct AI like you direct film\u2014with camera control, integrated pipeline, and real-time collaboration.'

  // Whether the 50%-off-first-month price promo is live. When it's off, the
  // invite still boosts first-month OLIVES, but the price never reverts — so
  // the "first month" price framing must not appear (only the olive framing).
  const promoActive = campaign.firstMonthPromoActive === true
  const dealPlans = getPromoDealPlans(campaign)
  const hasBonusOlives = dealPlans.some(planId => (campaign.rewards[planId]?.bonus_olives ?? 0) > 0)
  const expiryLabel = campaign.expires_at
    ? new Date(campaign.expires_at).toLocaleDateString(locale, { month: 'long', day: 'numeric' })
    : null

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-black">
      <VideoBackground />

      <section className="relative z-10 flex min-h-screen items-center justify-center px-6 md:px-8 lg:px-12">
        <div
          className={`w-full max-w-2xl text-center transition-opacity duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        >
          <div className="flex flex-col items-center">
            {/* Headline */}
            <h1
              className={`text-5xl font-semibold leading-[1.1] tracking-tight text-white transition-opacity duration-700 delay-75 sm:text-6xl md:text-7xl ${isVisible ? 'opacity-100' : 'opacity-0'}`}
            >
              {headline}
            </h1>

            {/* Tagline with inviter */}
            <p
              className={`mt-5 max-w-lg text-lg font-light leading-relaxed text-white/60 transition-opacity duration-700 delay-150 sm:mt-6 sm:text-xl md:text-2xl ${isVisible ? 'opacity-100' : 'opacity-0'}`}
            >
              {inviterName ? (
                <>
                  <span className="font-normal text-white/90">{inviterName}</span> invited you to Martini.
                  <br />
                  Skip the waitlist.
                </>
              ) : (
                description
              )}
            </p>

            {/* Deal box — only when there's a real perk to show (see getPromoDealPlans) */}
            {dealPlans.length > 0 && (
              <div
                className={`mt-8 w-full max-w-sm rounded-xl border border-white/10 bg-white/5 px-6 py-4 backdrop-blur-sm transition-opacity duration-700 delay-250 sm:mt-10 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
              >
                {expiryLabel && (
                  <p className="mb-1.5 text-xs font-medium tracking-wider text-white/60 uppercase">
                    Priority access <span className="text-white">expires</span>{' '}
                    <span className="font-semibold text-white">{expiryLabel}</span>
                  </p>
                )}
                {/* Scope, read before the numbers. With the price promo live it
                    covers price AND olives; without it, only the olive bonus is
                    first-month, so the price framing must not appear. The percent
                    is stated, not left to the strike-through arithmetic: it names
                    the offer, and matches the /pricing copy for the same coupon.
                    Rule of 100 — the % carries Pro ($30), the strike rows carry
                    the bigger absolute saving on Studio ($75 off). */}
                <p className="mb-3 text-xs text-white/55">
                  {promoActive ? (
                    <>
                      <span className="font-semibold text-white">50% off</span> your first month
                      {hasBonusOlives ? ', with your invite:' : ':'}
                    </>
                  ) : (
                    <>
                      <span className="font-semibold text-white">Bonus olives</span> with your invite:
                    </>
                  )}
                </p>
                <div className="space-y-3">
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="text-sm text-white/50">Indie · Free</span>
                    <span className="text-sm font-medium text-white">0 olives</span>
                  </div>
                  {dealPlans.map(planId => {
                    const bonus = campaign.rewards[planId]?.bonus_olives ?? 0
                    const base = PLAN_MONTHLY_OLIVES[planId] ?? 0
                    const price = PLAN_PRICES[planId]
                    const name = PLAN_NAMES[planId] ?? planId

                    return (
                      <div key={planId} className="flex items-baseline justify-between gap-4">
                        <span className="text-sm text-white/50">
                          {name} ·{' '}
                          {promoActive ? (
                            <>
                              {/* No /mo and no per-row "first month": the scope line
                                  above frames these as first-month values, so the
                                  strike alone carries the deal and rows never wrap. */}
                              <span className="font-normal text-white/30 line-through">${price}</span>{' '}
                              <span className="font-medium text-white/90">${price / 2}</span>
                            </>
                          ) : (
                            <>
                              <span className="font-medium text-white/90">${price}</span>/mo
                            </>
                          )}
                        </span>
                        <span className="text-sm font-medium text-white">
                          {bonus > 0 && (
                            <>
                              <span className="font-normal text-white/30 line-through">
                                {base.toLocaleString()}
                              </span>{' '}
                            </>
                          )}
                          {(base + bonus).toLocaleString()} olives
                        </span>
                      </div>
                    )
                  })}
                </div>
                {/* Quiet reversion note — present for honesty, demoted so it
                    never competes with the perks. Only claims a price reversion
                    when the price promo is actually live. */}
                <p className="mt-4 border-t border-white/[0.08] pt-3 text-xs text-white/35">
                  {promoActive
                    ? 'Then renews at your plan’s standard monthly rate.'
                    : 'A first-month boost — then your plan’s standard olive allowance.'}
                </p>
              </div>
            )}

            {/* CTA */}
            <div
              className={`mt-8 flex w-full flex-col items-center gap-5 transition-opacity duration-700 delay-300 sm:mt-10 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
            >
              <Button
                onClick={() => {
                  setModalMode('signup')
                  setShowSignUpModal(true)
                }}
                className="group h-12 w-full max-w-xs rounded-full border-0 bg-white text-sm font-semibold text-black transition-all duration-200 hover:bg-white/90 hover:shadow-[0_0_40px_rgba(255,255,255,0.1)] active:scale-[0.98] sm:h-14 sm:text-base"
              >
                <span className="flex items-center justify-center">
                  Get started
                  <ArrowRight className="ms-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </Button>

              <p className="text-sm text-white/40">
                Already have an account?{' '}
                <button
                  onClick={() => {
                    setModalMode('signin')
                    setShowSignUpModal(true)
                  }}
                  className="font-medium text-white/60 underline underline-offset-2 transition-colors hover:text-white"
                >
                  Log in
                </button>
              </p>
            </div>
          </div>
        </div>
      </section>

      <SignUpModal
        key={modalMode}
        isOpen={showSignUpModal}
        onClose={() => setShowSignUpModal(false)}
        returnPath={campaign.active ? '/select-plan' : undefined}
        defaultMode={modalMode}
        inviterName={inviterName}
      />
    </div>
  )
}
